import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import {
  showSuccessAlert,
  showErrorAlert,
  showInfoAlert,
} from "../../uiInterface/base-ui/alertservice";
import { CreditCard } from "lucide-react";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";

const DealsSettingsLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading Settings...</span>
  </div>
);

const route = all_routes;

type Stage = {
  id: string;
  name: string;
  order: number;
};

const initialLifecycle: { name: string; stages: Stage[] } = {
  name: "",
  stages: [],
};

const DealsSettings = () => {
  const initialRef = useRef<{ name: string; stages: Stage[] }>(
    initialLifecycle
  );
  const { lifecycleTypeNames, setLifecycleTypeNames } = useContext(AuthContext);
  const { auth, setAuth, selectedTenantId } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [lifecycle, setLifecycle] = useState<{ name: string; stages: Stage[] }>(
    initialLifecycle
  );
  const [lifecycleTypeId, setLifecycleTypeId] = useState<number | null>(null);
  const [tenantIdFromAPI, setTenantIdFromAPI] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleLifecycleNameChange = (value: string) => {
    setLifecycle((l) => ({ ...l, name: value }));
    setHasUnsavedChanges(true);
  };

  const moveStage = (sortedIdx: number, direction: number) => {
    const sorted = lifecycle.stages.slice().sort((a, b) => a.order - b.order);
    const targetIdx = sortedIdx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    [sorted[sortedIdx], sorted[targetIdx]] = [
      sorted[targetIdx],
      sorted[sortedIdx],
    ];
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setLifecycle((l) => ({ ...l, stages: reOrdered }));
    setHasUnsavedChanges(true);
  };

  const showNoChangeAlert = async (message: string) => {
    await showInfoAlert(message);
  };

  const updateStageName = (idx: number, value: string) => {
    const sorted = lifecycle.stages.slice().sort((a, b) => a.order - b.order);
    sorted[idx].name = value;
    setLifecycle((l) => ({
      ...l,
      stages: sorted.map((s, i) => ({ ...s, order: i + 1 })),
    }));
    setHasUnsavedChanges(true);
  };

  const addStage = () => {
    const newStage = {
      id: `s${Date.now()}`,
      name: "",
      order: lifecycle.stages.length + 1,
    };
    setLifecycle((l) => ({ ...l, stages: [...l.stages, newStage] }));
    setHasUnsavedChanges(true);
  };

  const removeStage = (sortedIdx: number) => {
    const sorted = lifecycle.stages.slice().sort((a, b) => a.order - b.order);
    sorted.splice(sortedIdx, 1);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setLifecycle((l) => ({ ...l, stages: reOrdered }));
    setHasUnsavedChanges(true);
  };

  const save = async () => {
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader || !tenantIdFromAPI || !lifecycleTypeId) {
      console.error("Missing required authentication or IDs.");
      return;
    }

    const trimmedName = lifecycle.name.trim();
    const nameChanged = trimmedName !== initialRef.current.name.trim();

    const nonEmpty = lifecycle.stages.filter((s) => s.name.trim() !== "");
    const sorted = nonEmpty.slice().sort((a, b) => a.order - b.order);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));

    const orders = reOrdered.map((s) => s.order);
    const unique = new Set(orders);
    if (unique.size !== orders.length) {
      await showErrorAlert("Each stage must have a unique order number.");
      return;
    }

    const originalIds = new Set(initialRef.current.stages.map((s) => s.id));
    const currentIds = new Set(reOrdered.map((s) => s.id));

    const deletes = initialRef.current.stages
      .filter((s) => !currentIds.has(s.id) && !s.id.startsWith("s"))
      .map((s) => ({ lifecycle_type_stage_id: Number(s.id) }));

    const inserts = reOrdered
      .filter((s) => s.id.startsWith("s") && s.name.trim() !== "")
      .map((s) => ({
        stage_name: s.name.trim(),
        stage_order: s.order,
        tenant_id: tenantIdFromAPI,
        lifecycle_type_id: lifecycleTypeId,
      }));

    const updates = reOrdered
      .filter((s) => !s.id.startsWith("s"))
      .map((s) => ({
        lifecycle_type_stage_id: Number(s.id),
        stage_name: s.name.trim(),
        stage_order: s.order,
      }));

    const hasStageChanges =
      inserts.length > 0 || updates.length > 0 || deletes.length > 0;

    try {
      if (nameChanged) {
        const patchRes = await fetch(
          `${API_BASE_URL}/lifecycle-type/${lifecycleTypeId}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
            body: JSON.stringify({ lifecycle_type_display_name: trimmedName }),
          }
        );

        if (!patchRes.ok) {
          const errorText = await patchRes.text();
          console.error("Lifecycle name update failed:", errorText);
          await showErrorAlert("Error updating lifecycle name.");
          return;
        }
        const updated = {
          ...lifecycleTypeNames,
          DEAL: trimmedName,
        };
        setLifecycleTypeNames(updated);
      }

      const payload = { updates, inserts, deletes };

      const response = await fetch(
        `${API_BASE_URL}/batch/lifecycle-type-stage/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Stage save error:", errorText);
        await showErrorAlert("Error saving lifecycle stage(s).");
        return;
      }

      let successMessage = "";
      if (nameChanged && hasStageChanges) {
        successMessage = "Lifecycle name and stage(s) updated successfully.";
      } else if (nameChanged) {
        successMessage = "Lifecycle name updated successfully.";
      } else {
        successMessage = "Stage(s) updated successfully.";
      }

      await showSuccessAlert(successMessage);
      setLifecycle((l) => ({ ...l, stages: reOrdered }));
      initialRef.current = { name: trimmedName, stages: reOrdered };
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Save error:", error);
      await showErrorAlert("An error occurred while saving.");
    }
  };

  const cancel = () => {
    setLifecycle(initialRef.current);
    setHasUnsavedChanges(false);
  };

  const hasChanges = useMemo(() => {
    const trimmedName = lifecycle.name.trim();
    const nameChanged = trimmedName !== initialRef.current.name.trim();

    const nonEmpty = lifecycle.stages.filter((s) => s.name.trim() !== "");
    const sorted = nonEmpty.slice().sort((a, b) => a.order - b.order);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));

    const originalIds = new Set(initialRef.current.stages.map((s) => s.id));
    const currentIds = new Set(reOrdered.map((s) => s.id));

    const deletes = initialRef.current.stages.filter(
      (s) => !currentIds.has(s.id) && !s.id.startsWith("s")
    );
    const inserts = reOrdered.filter(
      (s) => s.id.startsWith("s") && s.name.trim() !== ""
    );
    const updates = reOrdered.filter(
      (s) => originalIds.has(s.id) && !s.id.startsWith("s")
    );

    return (
      nameChanged ||
      inserts.length > 0 ||
      updates.length > 0 ||
      deletes.length > 0 ||
      hasUnsavedChanges
    );
  }, [lifecycle, hasUnsavedChanges]);
  useEffect(() => {
    if (!selectedTenantId) return;
    const load = async () => {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader) return;

      setIsLoading(true);
      try {
        const qt = await fetch(
          `${API_BASE_URL}/lifecycle-type/?lifecycle_type_key=DEAL&tenant_id=${selectedTenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        const qj = await qt.json();
        const lt = qj.results[0];
        setLifecycleTypeId(lt.lifecycle_type_id);
        setTenantIdFromAPI(lt.tenant_id);

        const st = await fetch(
          `${API_BASE_URL}/batch/lifecycle-type-stage/?lifecycle_type_id=${lt.lifecycle_type_id}&tenant_id=${lt.tenant_id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        const sj: any[] = await st.json();
        const formatted = sj
          .sort((a, b) => a.stage_order - b.stage_order)
          .map((s) => ({
            id: String(s.lifecycle_type_stage_id),
            name: s.stage_name,
            order: s.stage_order,
          }));

        const name = lt.lifecycle_type_display_name || "Deal";
        setLifecycle({ name, stages: formatted });
        initialRef.current = { name: name.trim(), stages: formatted };
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error("Error loading deals lifecycle:", err);
        await showErrorAlert("Failed to load deals settings.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [selectedTenantId]);

  const sortedStages = lifecycle.stages
    .slice()
    .sort((a, b) => a.order - b.order);

  const userRole = useTenantRole(selectedTenantId || 0);

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">Settings</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              {/* Settings Menu */}
              <div className="card">
                <div className="card-body pb-0 pt-2">
                  <SettingsTabsNavigation
                    activeTab="lifecycle"
                    variant="bottom"
                    includeIcons={true}
                    context="organization"
                  />
                </div>
              </div>
              {/* /Settings Menu */}
              <div className="row">
                <div className="col-xl-3 col-lg-12 theiaStickySidebar">
                  {/* Settings Sidebar */}
                  <div className="card">
                    <div className="card-body">
                      <div className="settings-sidebar">
                        <h4 className="fw-semibold mb-3">
                          {" "}
                          LifeCycle Settings
                        </h4>
                        <div className="list-group list-group-flush settings-sidebar">
                          <Link to={route.leadssettings} className="fw-medium">
                            Leads
                          </Link>
                          <Link
                            to={route.pipelinesettings}
                            className="fw-medium"
                          >
                            Pipelines
                          </Link>
                          <Link
                            to={route.dealssettings}
                            className="fw-medium active"
                          >
                            Deals
                          </Link>
                          <Link
                            to={route.proposalssettings}
                            className="fw-medium"
                          >
                            Proposals
                          </Link>
                          <Link
                            to={route.contractssettings}
                            className="fw-medium"
                          >
                            Contracts
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Settings Sidebar */}
                </div>
                <div className="col-xl-9 col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      {isLoading ? (
                        <DealsSettingsLoader />
                      ) : (
                        <>
                          <div className="border-bottom mb-3 pb-3">
                            <h4 className="fw-semibold mb-1">
                              {lifecycle.name || "Deals"} Settings
                            </h4>
                            <p>
                              Configure the{" "}
                              {lifecycle.name.toLowerCase() || "Deals"}{" "}
                              information below
                            </p>
                          </div>
                          {/* LifeCycle Name */}
                          <div className="mb-4">
                            <label className="form-label fw-medium">
                              LifeCycle Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              disabled={userRole?.role_key === "SALESUSER"}
                              value={lifecycle.name}
                              onChange={(e) =>
                                handleLifecycleNameChange(e.target.value)
                              }
                            />
                          </div>

                          {/* Stages List */}
                          <div className="mb-3">
                            <label className="form-label fw-medium">
                              LifeCycle Stages
                            </label>
                          </div>
                          {sortedStages.map((stage, idx) => (
                            <div
                              key={stage.id}
                              className="d-flex align-items-center mb-2 p-2 border rounded w-20"
                            >
                              <input
                                type="text"
                                className="form-control me-3"
                                placeholder="Stage name"
                                disabled={userRole?.role_key === "SALESUSER"}
                                value={stage.name}
                                onChange={(e) =>
                                  updateStageName(idx, e.target.value)
                                }
                              />
                              {userRole?.role_key === "CLIENTADMIN" &&
                                "SUPERADMIN" &&
                                "PLATFORM_ANALYST" && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary me-1"
                                      onClick={(e) => {
                                        moveStage(idx, -1);
                                        e.currentTarget.blur();
                                      }}
                                      disabled={idx === 0}
                                    >
                                      ▲
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-secondary me-3"
                                      onClick={(e) => {
                                        moveStage(idx, 1);
                                        e.currentTarget.blur();
                                      }}
                                      disabled={idx === sortedStages.length - 1}
                                    >
                                      ▼
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={(e) => {
                                        removeStage(idx);
                                        e.currentTarget.blur();
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </>
                                )}
                            </div>
                          ))}

                          {/* Add Stage */}
                          {userRole?.role_key === "CLIENTADMIN" &&
                            "SUPERADMIN" &&
                            "PLATFORM_ANALYST" && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-sm mt-2 mb-4"
                                  onClick={(e) => {
                                    addStage();
                                    e.currentTarget.blur();
                                  }}
                                >
                                  <i className="fa fa-plus" /> Add Stage
                                </button>

                                {/* Actions */}
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary me-2"
                                    onClick={(e) => {
                                      cancel();
                                      e.currentTarget.blur();
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary me-2"
                                    onClick={(e) => {
                                      save();
                                      e.currentTarget.blur();
                                    }}
                                  >
                                    Save Changes
                                  </button>
                                </div>
                              </>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
    </div>
  );
};

export default DealsSettings;
